import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { UnitfieldIcon } from '@unitfield/ui/icons';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@unitfield/ui/components/ui/carousel';
import { ListingDescription } from '@unitfield/ui/components/listings/listing-description';
import { LoadingRegion } from '@unitfield/ui/components/ui/pixel-grid';
import { cn } from '@unitfield/ui/lib/utils';
import { resolveContentState } from '../lib/content-state';
import { PublicRouteLink, usePublicRouteBridge } from '../public-route-bridge';
import {
  publicRentalDataPaths,
  publicUnitDetailQueryKey,
  rentalApplicationPath,
  rentalListingPath,
  type PublicRentalApplicationLocationState,
} from '../public-route-contracts';
import { PublicFooter, PublicSpinner, PublicTopBar, telHref, usePublicShell } from '../shell';
import shell from '../shell/shell.module.css';
import { DateWell } from '../shell/date-well';
import { unitLocationState } from './listing-cards';
import {
  describeMoveInFit,
  formatArea,
  formatBathsShort,
  formatBedsShort,
  formatShortDate,
  formatRent,
  mapsSearchUrl,
  parseIsoDate,
  startOfDay,
  toIsoDate,
  unitAddress,
  unitPhotos,
  unitTitle,
  type PublicListingUnit,
} from './listing-model';
import styles from './unit-page.module.css';

export interface PublicRentalUnitCoreProps {
  organizationSlug: string;
  unitPublicKey: string;
  locationState?: unknown;
  /** Stories pin the day; the page uses today. */
  now?: Date;
}

/** The listing's card snapshot, so the page paints before the detail call returns. */
function unitFromLocationState(state: unknown, unitPublicKey: string): PublicListingUnit | undefined {
  const unit = (state as PublicRentalApplicationLocationState | null | undefined)?.publicRentalApplicationUnit;
  if (!unit || unit.publicUnitKey !== unitPublicKey) return undefined;
  return {
    publicUnitKey: unit.publicUnitKey,
    propertyName: unit.propertyName,
    unitName: unit.unitName,
    address1: unit.addressLine1,
    address2: unit.addressLine2,
    city: unit.city,
    state: unit.state,
    postalCode: unit.postalCode,
    bedrooms: unit.bedrooms,
    bathrooms: unit.bathrooms,
    areaSqft: unit.areaSqft,
    rent: unit.rent,
    availableFrom: unit.availableFrom,
    applyEligible: unit.applyEligible,
    photoUrls: unit.photoUrls,
    coverImageUrl: unit.coverImageUrl,
    description: unit.description,
  };
}

async function fetchUnit(unitPublicKey: string, signal?: AbortSignal): Promise<PublicListingUnit> {
  const response = await fetch(
    publicRentalDataPaths.unit(unitPublicKey),
    { signal },
  );
  const result = await response.json().catch(() => null) as { success?: boolean; message?: string; data?: unknown } | null;
  if (!response.ok || !result?.success || !result.data) throw new Error(result?.message || 'This home could not be loaded.');
  return result.data as PublicListingUnit;
}

function Gallery({ photos, start, title, onClose }: { photos: string[]; start: number; title: string; onClose: () => void }) {
  const [api, setApi] = useState<CarouselApi>();
  const [index, setIndex] = useState(start);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') api?.scrollNext();
      if (event.key === 'ArrowLeft') api?.scrollPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [api, onClose]);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setIndex(api.selectedScrollSnap());
    api.on('select', onSelect);
    return () => { api.off('select', onSelect); };
  }, [api]);

  // Portalled to <body>: the shell's container query would otherwise capture `position: fixed`.
  // The gallery uses fixed colours only, so it needs none of the shell's tokens.
  return createPortal(
    <div className={styles.gallery} role="dialog" aria-modal="true" aria-label={`Photos of ${title}`}>
      <div className={styles.galleryBar}>
        <span className={cn(styles.count, styles.num)}>{index + 1} / {photos.length}</span>
        <button ref={closeRef} type="button" className={styles.galleryButton} onClick={onClose} aria-label="Close photos">
          <UnitfieldIcon name="close" size={18} aria-hidden="true" />
        </button>
      </div>
      <Carousel setApi={setApi} opts={{ startIndex: start, loop: photos.length > 1 }} className={styles.galleryTrack}>
        <CarouselContent className="ml-0 h-full">
          {photos.map((src, photoIndex) => (
            <CarouselItem key={`${src}-${photoIndex}`} className="flex h-full basis-full items-center justify-center pl-0">
              <img src={src} alt={`Photo ${photoIndex + 1} of ${photos.length}`} className={styles.galleryPhoto} draggable={false} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      {photos.length > 1 ? (
        <>
          <button type="button" className={cn(styles.galleryButton, styles.galleryPrev)} onClick={() => api?.scrollPrev()} aria-label="Previous photo">
            <UnitfieldIcon name="chevron-left" size={20} aria-hidden="true" />
          </button>
          <button type="button" className={cn(styles.galleryButton, styles.galleryNext)} onClick={() => api?.scrollNext()} aria-label="Next photo">
            <UnitfieldIcon name="chevron-right" size={20} aria-hidden="true" />
          </button>
        </>
      ) : null}
    </div>,
    document.body,
  );
}

function Filmstrip({ photos, onOpen }: { photos: string[]; onOpen: (index: number) => void }) {
  const shown = photos.slice(0, 4);
  const more = photos.length - shown.length;
  return (
    <div className={styles.strip}>
      {shown.map((src, index) => (
        <button key={`${src}-${index}`} type="button" className={styles.stripPhoto} data-current={index === 0 ? '' : undefined} onClick={() => onOpen(index)} aria-label={`Open photo ${index + 1} of ${photos.length}`}>
          <img src={src} alt="" draggable={false} />
        </button>
      ))}
      {more > 0 ? (
        <button type="button" className={styles.stripMore} onClick={() => onOpen(shown.length)} aria-label={`Open all ${photos.length} photos`}>
          <span className={styles.num}>+{more}</span>
        </button>
      ) : null}
    </div>
  );
}

export function PublicRentalUnitView({ organizationSlug, unitPublicKey, locationState, now }: PublicRentalUnitCoreProps) {
  const { searchParams } = usePublicRouteBridge();
  const { branding } = usePublicShell();
  const today = useMemo(() => startOfDay(now ?? new Date()), [now]);
  const snapshot = useMemo(() => unitFromLocationState(locationState, unitPublicKey), [locationState, unitPublicKey]);
  const unitQuery = useQuery({
    queryKey: publicUnitDetailQueryKey(organizationSlug, unitPublicKey),
    enabled: Boolean(organizationSlug && unitPublicKey),
    staleTime: 5 * 60 * 1000,
    placeholderData: snapshot,
    queryFn: ({ signal }) => fetchUnit(unitPublicKey, signal),
  });
  const unitContentState = resolveContentState({ data: unitQuery.data, isLoading: unitQuery.isLoading, error: unitQuery.error });
  const unit = unitQuery.data;

  const urlMoveIn = parseIsoDate(searchParams.get('moveIn'));
  const available = parseIsoDate(unit?.availableFrom);
  const defaultMoveIn = urlMoveIn ?? (available && available > today ? available : today);
  const [chosen, setChosen] = useState<string | null>(null);
  const moveIn = parseIsoDate(chosen) ?? defaultMoveIn;
  const [galleryStart, setGalleryStart] = useState<number | null>(null);

  const listingHref = (() => {
    const moveInParam = searchParams.get('moveIn');
    return moveInParam && urlMoveIn ? `${rentalListingPath(organizationSlug)}?moveIn=${moveInParam}` : rentalListingPath(organizationSlug);
  })();
  const backPill = (
    <PublicRouteLink to={listingHref} className={shell.pill}>
      <UnitfieldIcon name="chevron-left" size={16} aria-hidden="true" />
      All homes
    </PublicRouteLink>
  );

  if (unitContentState.status !== 'ready' || !unit) {
    return (
      <div className={shell.page}>
        <PublicTopBar trailing={backPill} />
        {unitContentState.status === 'loading' ? (
          <LoadingRegion busy label="Loading this home" className={styles.loading} indicator={<PublicSpinner size={36} label="Loading this home" />} />
        ) : (
          <div className={styles.missing} role="alert">
            <h1>This home isn&rsquo;t listed right now.</h1>
            <PublicRouteLink to={rentalListingPath(organizationSlug)} className={cn(shell.btn, shell.btnTonal)}>See all homes</PublicRouteLink>
          </div>
        )}
        <PublicFooter />
      </div>
    );
  }

  const title = unitTitle(unit);
  const address = unitAddress(unit);
  const photos = unitPhotos(unit);
  const rent = formatRent(unit.rent);
  const eligible = unit.applyEligible !== false;
  const fit = describeMoveInFit(unit, moveIn, today);
  const facts = [
    { icon: 'bed', label: formatBedsShort(unit.bedrooms) },
    { icon: 'bath', label: formatBathsShort(unit.bathrooms) },
    { icon: 'ruler', label: formatArea(unit.areaSqft) },
  ].filter((fact): fact is { icon: string; label: string } => Boolean(fact.label));
  const amenities = Array.from(new Set((unit.amenities ?? []).map((item) => item.trim()).filter(Boolean)));
  const maps = mapsSearchUrl(unit);
  const place = unit.city?.trim() || address;
  const moveInIso = toIsoDate(moveIn);
  const applyHref = `${rentalApplicationPath(organizationSlug, unit.applyKey ?? unitPublicKey)}?moveIn=${moveInIso}`;
  const applyState = { publicRentalApplicationUnit: { ...unitLocationState(unit, branding.name), publicUnitKey: unitPublicKey } };
  const hasContact = Boolean(branding.contactPhone || branding.contactEmail);
  const availability = !eligible
    ? 'Not taking applications'
    : available && available > today ? `Available ${formatShortDate(available, today)}` : 'Available now';

  const applyAction = (className?: string, label = 'Apply for this home') => eligible ? (
    <PublicRouteLink to={applyHref} state={applyState} className={cn(shell.btn, shell.btnBrand, className)}>{label}</PublicRouteLink>
  ) : (
    <button type="button" className={cn(shell.btn, shell.btnBrand, className)} disabled>{label}</button>
  );

  return (
    <div className={styles.page}>
      <div className={cn(shell.page, styles.pageInner)}>
        <div className={styles.desktopBar}><PublicTopBar trailing={backPill} /></div>

        <section className={styles.hero} aria-label="Photos">
          {photos[0] ? (
            <button type="button" className={styles.heroPhoto} onClick={() => setGalleryStart(0)} aria-label={`Open ${photos.length} photos`}>
              <img src={photos[0]} alt="" draggable={false} />
            </button>
          ) : <span className={styles.heroPhoto} />}
          <PublicRouteLink to={listingHref} className={styles.phoneBack} aria-label="All homes">
            <UnitfieldIcon name="chevron-left" size={18} aria-hidden="true" />
          </PublicRouteLink>
          {photos.length > 0 ? (
            <span className={cn(styles.count, styles.phoneCount, styles.num)} aria-hidden="true">
              <UnitfieldIcon name="image" size={14} aria-hidden="true" />1 / {photos.length}
            </span>
          ) : null}
          <div className={styles.heroText}>
            <h1>{title}</h1>
            {address ? <p className={styles.address}>{address}</p> : null}
            {facts.length > 0 ? (
              <div className={styles.glassRow}>
                {facts.map((fact) => (
                  <span key={fact.icon} className={cn(styles.glass, styles.num)}>
                    <UnitfieldIcon name={fact.icon} size={16} aria-hidden="true" />{fact.label}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          {photos.length > 1 ? <Filmstrip photos={photos} onOpen={setGalleryStart} /> : null}
        </section>

        <div className={styles.columns}>
          <div className={styles.about}>
            {unit.description?.trim() ? <ListingDescription value={unit.description.trim()} className={styles.description} /> : null}
            {amenities.length > 0 ? (
              <>
                <h2 className={styles.subhead}>What&rsquo;s included</h2>
                <ul className={styles.amenities}>
                  {amenities.map((amenity) => <li key={amenity}>{amenity}</li>)}
                </ul>
              </>
            ) : null}
            {place ? (
              <div className={styles.where}>
                <span className={styles.tile}><UnitfieldIcon name="location" size={18} aria-hidden="true" /></span>
                <span>
                  {place}
                  {maps ? <> · <a className={shell.link} href={maps} target="_blank" rel="noreferrer">Open in Maps</a></> : null}
                </span>
              </div>
            ) : null}
          </div>

          <aside className={styles.decide} aria-label="Apply">
            {rent ? (
              <p className={styles.decideRent}><span className={styles.num}>{rent}</span><small> / month</small></p>
            ) : null}
            {eligible ? (
              <>
                <label className={styles.decideLabel} htmlFor="public-unit-move-in">When do you want to move in?</label>
                <DateWell
                  id="public-unit-move-in"
                  className={styles.dateWell}
                  value={moveInIso}
                  min={toIsoDate(today)}
                  clearable={false}
                  onChange={(value) => {
                    const date = parseIsoDate(value);
                    if (date && date >= today) setChosen(value);
                  }}
                />
                <p className={styles.fitLine} data-tone={fit.tone}>
                  <span className={styles.fitMark} aria-hidden="true">
                    <UnitfieldIcon name={fit.tone === 'ok' ? 'check' : 'clock'} size={12} />
                  </span>
                  {fit.text}
                </p>
              </>
            ) : (
              <p className={styles.closed}>Not taking applications right now</p>
            )}
            {applyAction(cn(shell.btnFull, styles.decideApply))}
            {eligible ? <p className={styles.fine}>About 10 minutes · Saves as you go</p> : null}
            {hasContact ? (
              <div className={styles.ask}>
                <div>
                  <b>Questions about this home?</b>
                  {branding.name ? <span>{branding.name}</span> : null}
                </div>
                {branding.contactPhone ? (
                  <a className={cn(shell.pill, styles.askPill)} href={telHref(branding.contactPhone)}>
                    <UnitfieldIcon name="telephone" size={16} aria-hidden="true" />Call
                  </a>
                ) : branding.contactEmail ? (
                  <a className={cn(shell.pill, styles.askPill)} href={`mailto:${branding.contactEmail}`}>
                    <UnitfieldIcon name="mail" size={16} aria-hidden="true" />Email
                  </a>
                ) : null}
              </div>
            ) : null}
          </aside>
        </div>

        <PublicFooter />
      </div>

      <div className={styles.phoneBar}>
        <div className={styles.phoneBarText}>
          {rent ? <b><span className={styles.num}>{rent}</span><small> / mo</small></b> : null}
          <span>{availability}</span>
        </div>
        {applyAction(cn(shell.btnSmall, styles.phoneApply), 'Apply now')}
      </div>

      {galleryStart !== null && photos.length > 0 ? (
        <Gallery photos={photos} start={galleryStart} title={title} onClose={() => setGalleryStart(null)} />
      ) : null}
    </div>
  );
}
